import type { AuthTokens } from '@/lib/types/auth';

export type PerformTokenRefresh = (
  currentRefreshToken: string,
) => Promise<AuthTokens>;

/**
 * Single-flights token refreshes: while a network refresh is in flight, concurrent
 * callers share the same promise instead of each hitting the network.
 *
 * Zitadel rotates the refresh token on use (one-time), so overlapping network
 * refreshes make all-but-the-first fail with `invalid_grant`. On a cold launch three
 * refreshers fire at once (auth restore effect, Apollo error link, PowerSync
 * connector); without deduping, the losers' failures cascade into a self-inflicted
 * logout that flickers the app to the login screen. Collapsing them to one call keeps
 * the rotated token valid for the single winner and hands everyone the same tokens.
 */
export class TokenRefresher {
  private inFlight: Promise<AuthTokens> | null = null;

  constructor(private readonly performRefresh: PerformTokenRefresh) {}

  refresh(currentRefreshToken: string): Promise<AuthTokens> {
    if (this.inFlight) {
      return this.inFlight;
    }

    const promise = this.performRefresh(currentRefreshToken).finally(() => {
      // Only clear if still the active flight — a settled promise must not evict a
      // newer refresh that started after it resolved.
      if (this.inFlight === promise) {
        this.inFlight = null;
      }
    });
    this.inFlight = promise;

    return promise;
  }
}
